import { PersonAdd } from '@mui/icons-material';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { getCompanyUsers, getOtherCompanyUsers, updateUser } from 'lib/api';
import { updateSigner } from 'lib/api/users';
import { UpdateSignerBody } from 'lib/api/users/types';
import {
  COUNTRY_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  PROFESSION_OPTIONS,
} from 'lib/consts/usersFields';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import DialogTitle from 'lib/designSystem/Title/DialogTitle';
import { errorHandler } from 'lib/functions/errors';
import {
  buildSignerValidation,
  isEnumKey,
  isValidChileanPhone,
  isValidEmail,
  showAlert,
  validaRut,
} from 'lib/functions/utils';
import { User } from 'lib/types/models';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  businessIdentifier?: string;
}

type Signer = Omit<UpdateSignerBody, 'businessIdentifier'>;

const AssignSigner: React.FC<Props> = ({
  open,
  setOpen,
  businessIdentifier,
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [signerData, setSignerData] = useState<Signer | undefined>(undefined);

  const nonSigners = useMemo(() => {
    return users.filter((user) => !user.isSigner);
  }, [users]);

  const fetchCompanyUsers = async () => {
    try {
      setLoading(true);
      if (businessIdentifier) {
        const users = await getOtherCompanyUsers(dispatch, businessIdentifier);
        setUsers(users);
      } else {
        const users = await getCompanyUsers(dispatch);
        setUsers(users);
      }
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const { hasError: emailHasError, message: emailErrorMessage } =
    buildSignerValidation(
      !signerData?.email?.length,
      !!signerData?.email?.length && !isValidEmail(signerData?.email),
      'Email obligatorio',
      'Email inválido',
    );

  const { hasError: identifierHasError, message: identifierErrorMessage } =
    buildSignerValidation(
      !signerData?.identifier?.length,
      !!signerData?.identifier?.length && !validaRut(signerData.identifier),
      'RUT obligatorio',
      'RUT inválido',
    );

  const { hasError: phoneHasError, message: phoneErrorMessage } =
    buildSignerValidation(
      !signerData?.phone?.length,
      !!signerData?.phone?.length && !isValidChileanPhone(signerData.phone),
      'Teléfono obligatorio',
      'Teléfono inválido (569XXXXXXXX)',
    );

  const { hasError: countryHasError, message: countryErrorMessage } =
    buildSignerValidation(
      !signerData?.country?.length,
      !!signerData?.country?.length &&
        !isEnumKey(COUNTRY_OPTIONS, signerData.country),
      'País obligatorio',
      'País inválido',
    );

  const { hasError: professionHasError, message: professionErrorMessage } =
    buildSignerValidation(
      !signerData?.profession?.length,
      false,
      'Profesión obligatoria',
      'Profesión inválida',
    );

  const { hasError: maritalHasError, message: maritalErrorMessage } =
    buildSignerValidation(
      !signerData?.maritalStatus?.length,
      !!signerData?.maritalStatus?.length &&
        !isEnumKey(MARITAL_STATUS_OPTIONS, signerData.maritalStatus),
      'Estado civil obligatorio',
      'Estado civil inválido',
    );

  const hasErrors =
    emailHasError ||
    phoneHasError ||
    identifierHasError ||
    countryHasError ||
    professionHasError ||
    maritalHasError;

  const handleAssignSigner = async () => {
    try {
      if (hasErrors) {
        let errorMessage = '';
        if (emailHasError) errorMessage = emailErrorMessage;
        else if (phoneHasError) errorMessage = phoneErrorMessage;
        else if (identifierHasError) errorMessage = identifierErrorMessage;
        else if (countryHasError) errorMessage = countryErrorMessage;
        else if (professionHasError) errorMessage = professionErrorMessage;
        else if (maritalHasError) errorMessage = maritalErrorMessage;
        showAlert(dispatch, 'error', errorMessage);
        return;
      }
      setLoading(true);
      const payload = {
        ...signerData,
        isSigner: true,
      };
      if (businessIdentifier) {
        await updateSigner(dispatch, payload, businessIdentifier);
      } else {
        await updateUser(dispatch, payload);
      }
      await fetchCompanyUsers();
      void queryClient.invalidateQueries({
        queryKey: ['businessProfile', 'signers', businessIdentifier],
      });
      showAlert(dispatch, 'success', 'Firmante asignado correctamente');
      setOpen(false);
      setSignerData(undefined);
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { value } = e.target;
    const user = users.find((user) => user.id === Number(value));
    if (user) {
      setSignerData({
        id: user.id,
        identifier: user.identifier ?? '',
        email: user.email ?? '',
        phone: user.phone ?? '',
        country: user.country ?? null,
        profession: user.profession ?? null,
        maritalStatus: user.maritalStatus ?? null,
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSignerData((prev: Signer | undefined) =>
      prev
        ? {
            ...prev,
            [name]: value,
          }
        : { [name]: value },
    );
  };

  const handleAutocompleteChange =
    (field: keyof Signer) =>
    (
      _event: React.SyntheticEvent<Element, Event>,
      value: Signer[keyof Signer] | null,
    ) => {
      setSignerData((prev: Signer | undefined) =>
        prev
          ? {
              ...prev,
              [field]: value ?? prev[field],
            }
          : { [field]: value },
      );
    };

  const getLabel = (value: string, options: Record<string, string>) => {
    return options[value] ?? '';
  };

  useEffect(() => {
    void fetchCompanyUsers();
  }, [dispatch]);

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle
        onClose={() => setOpen(false)}
        title="Asignar firmante"
        icon={<PersonAdd color="primary" />}
        color="primary"
      />
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <FormControl fullWidth margin="dense">
              <InputLabel id="signer-label">Firmante</InputLabel>
              <Select
                labelId="signer-label"
                id="signer-select"
                label="Firmante"
                value={signerData?.id?.toString() || ''}
                onChange={handleSelectChange}
              >
                {nonSigners.length === 0 ? (
                  <MenuItem disabled>No hay firmantes</MenuItem>
                ) : (
                  nonSigners.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="RUT"
              name="identifier"
              value={signerData?.identifier}
              onChange={handleInputChange}
              margin="dense"
              fullWidth
              error={identifierHasError}
              helperText={identifierHasError ? identifierErrorMessage : ''}
            />
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              options={Object.keys(COUNTRY_OPTIONS)}
              value={signerData?.country ?? null}
              onChange={handleAutocompleteChange('country')}
              getOptionLabel={(option) => getLabel(option, COUNTRY_OPTIONS)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Nacionalidad"
                  margin="dense"
                  fullWidth
                  error={countryHasError}
                  helperText={countryHasError ? countryErrorMessage : ''}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Email"
              name="email"
              type="email"
              value={signerData?.email ?? ''}
              onChange={handleInputChange}
              margin="dense"
              fullWidth
              error={emailHasError}
              helperText={emailHasError ? emailErrorMessage : ''}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Teléfono"
              name="phone"
              placeholder="56912345678"
              value={signerData?.phone}
              onChange={handleInputChange}
              margin="dense"
              fullWidth
              error={phoneHasError}
              helperText={phoneHasError ? phoneErrorMessage : ''}
            />
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              freeSolo
              options={PROFESSION_OPTIONS}
              value={signerData?.profession ?? null}
              onChange={handleAutocompleteChange('profession')}
              onInputChange={(_event, newInputValue, reason) => {
                if (reason === 'input' || reason === 'clear') {
                  setSignerData((prev: Signer | undefined) =>
                    prev
                      ? {
                          ...prev,
                          profession: newInputValue,
                        }
                      : { profession: newInputValue },
                  );
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Profesión"
                  margin="dense"
                  fullWidth
                  error={professionHasError}
                  helperText={professionHasError ? professionErrorMessage : ''}
                  onBlur={(e) => {
                    const value = e.target.value;
                    if (value) {
                      setSignerData((prev: Signer | undefined) =>
                        prev
                          ? {
                              ...prev,
                              profession: value,
                            }
                          : { profession: value },
                      );
                    }
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              options={Object.keys(MARITAL_STATUS_OPTIONS)}
              value={signerData?.maritalStatus ?? null}
              onChange={handleAutocompleteChange('maritalStatus')}
              getOptionLabel={(option) =>
                getLabel(option, MARITAL_STATUS_OPTIONS)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Estado civil"
                  margin="dense"
                  fullWidth
                  error={maritalHasError}
                  helperText={maritalHasError ? maritalErrorMessage : ''}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <CustomButton
          color="primary"
          loading={loading}
          onClick={handleAssignSigner}
        >
          Asignar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default AssignSigner;
